import { collection, onSnapshot, orderBy, query, type Unsubscribe } from 'firebase/firestore';
import { PrinterProfileSchema, type PrinterProfile } from '@etiquetachef/types';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'admin/firestorePrinters';

/** Estado da aba Impressoras (read-only) do detalhe do tenant. */
export type PrinterListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; printers: PrinterProfile[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lista impressoras do tenant ordenadas por `name asc`. Single-field index —
 * não precisa de composto em `firestore.indexes.json`. Docs que falham no
 * parse Zod ficam logados e ignorados (defesa em profundidade contra docs
 * antigos durante migrações de schema). Read-only: o admin só observa; toda
 * mutação de impressora acontece no Android (código de impressão congelado).
 *
 * Origem: sem issue — commit a55e77f4c, 2026-06-10 (aba Impressoras read-only).
 *
 * Contrato: listener em `tenants/{tenantId}/printers` por `name`; erro de snapshot vira `ERROR` (a tela mostra o alerta).
 *
 * Usado por: `features/printers/usePrinters.ts`.
 *
 * Arquitetura: rule `match /printers/{printerId}` sob `match /tenants/{tenantId}`.
 *
 * Dados: `tenants/{tid}/printers/{printerId}` — schema `PrinterProfileSchema`.
 *
 * Falhas conhecidas:
 *  - Impressora fora do schema some só com `console.warn`, sem report (#926).
 */
export function subscribePrinters(
  tenantId: string,
  onResult: (r: PrinterListResult) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe tenant=${tenantId}`);
  const ref = collection(firestore, 'tenants', tenantId, 'printers');
  const q = query(ref, orderBy('name', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      const printers: PrinterProfile[] = [];
      for (const d of snap.docs) {
        // Normaliza Timestamp→millis antes do parse Zod (campos de tempo
        // gravados via server podem chegar como `Timestamp` no SDK web).
        const parsed = PrinterProfileSchema.safeParse({ id: d.id, ...snapshotData(d) });
        if (parsed.success) {
          printers.push(parsed.data);
        } else {
          console.warn(`${TAG}: printer ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', printers });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
